
import React, { useEffect } from 'react';
import { useLocationInputs } from '@/hooks/ride/useLocationInputs';
import LocationInput from './LocationInput';
import type { RouteEndpoints } from './routeEndpoints';

export type { RouteEndpoints } from './routeEndpoints';

interface LocationInputsProps {
  pickupLocation: string;
  destination: string;
  setPickupLocation: (location: string) => void;
  setDestination: (destination: string) => void;
  /**
   * Both endpoints, once the user has finished choosing them — or null while
   * either is still being typed. The parent uses this to decide when a route
   * is worth drawing; routing half-typed text is what made the map show
   * "Failed to load route" mid-keystroke.
   */
  onEndpointsChange?: (endpoints: RouteEndpoints | null) => void;
}

const LocationInputs: React.FC<LocationInputsProps> = ({
  pickupLocation,
  destination,
  setPickupLocation,
  setDestination,
  onEndpointsChange,
}) => {
  const {
    // States
    localPickup,
    localDestination,
    pickupSuggestions,
    destinationSuggestions,
    recentLocations,
    
    // Actions
    setLocalPickup,
    setLocalDestination,
    handleCurrentLocation,
    handleSelectPickupLocation,
    handleSelectDestination,

    // Commit tracking
    commitPickup,
    commitDestination,
    routeEndpoints,

    // Status
    isPickupLoading,
    isDestinationLoading,
    isGettingLocation,
  } = useLocationInputs({
    initialPickup: pickupLocation,
    initialDestination: destination,
    onPickupChange: setPickupLocation,
    onDestinationChange: setDestination
  });

  useEffect(() => {
    onEndpointsChange?.(routeEndpoints);
  }, [routeEndpoints, onEndpointsChange]);

  return (
    <div className="space-y-4">
      <LocationInput
        value={localPickup}
        onChange={setLocalPickup}
        placeholder="Pickup location"
        icon={<div className="h-3 w-3 rounded-full bg-green-500"></div>}
        suggestions={pickupSuggestions}
        recentLocations={recentLocations}
        showCurrentLocation={true}
        onCurrentLocation={handleCurrentLocation}
        isGettingCurrentLocation={isGettingLocation}
        isLoading={isPickupLoading}
        onSelect={handleSelectPickupLocation}
        onCommit={commitPickup}
      />

      <LocationInput
        value={localDestination}
        onChange={setLocalDestination}
        placeholder="Enter destination"
        icon={<div className="h-3 w-3 rounded-full bg-ride-primary"></div>}
        suggestions={destinationSuggestions}
        recentLocations={recentLocations}
        isLoading={isDestinationLoading}
        onSelect={handleSelectDestination}
        onCommit={commitDestination}
      />
    </div>
  );
};

export default LocationInputs;
